export const siteData = {
	companyName: "SaskTech Lab",
	description:
		"Engineering solutions across software, hardware, AI, and IT systems with a mentorship-driven talent development model.",
	location: "Regina, Saskatchewan, Canada",
	email: "info@sasktechlab.ca",
	phone: "+1 (581) 701-0001",
	phoneHref: "tel:+15817010001",
	addressLines: ["I-4212 Castle Rd", "Regina, SK S4S 4W1", "Canada"],
	primaryCta: { label: "Work With Us", to: "/contact" },
	secondaryCta: { label: "Explore Projects", to: "/projects" },
};

export const navigationItems = [
	{ label: "Home", to: "/" },
	{ label: "About Us", to: "/about" },
	{ label: "Services", to: "/services" },
	{ label: "Our Model", to: "/ourmodel" },
	{ label: "Projects", to: "/projects" },
	{ label: "Careers", to: "/careers" },
	{ label: "Contact", to: "/contact" },
];

export const heroMetrics = [
	{ value: "25+", label: "Projects Completed" },
	{ value: "15+", label: "Graduates Supported" },
	{ value: "10+", label: "Industry Mentors" },
	{ value: "90%", label: "Client Satisfaction" },
];

export const homeApproach = [
	"SaskTech Lab combines commercial engineering delivery with workforce development so clients get practical results while graduates gain meaningful experience.",
	"The model is built for organizations that need real software, hardware, AI, or IT support but also value disciplined execution and local talent growth.",
	"Experienced professionals guide project work, review deliverables, and help translate emerging talent into dependable contributors.",
];

export const homeWorkflow = [
	{ title: "Project Intake", description: "We clarify the scope, business goals, and technical constraints before work starts." },
	{ title: "Team Assembly", description: "We align junior contributors and mentors around the project requirements." },
	{ title: "Guided Execution", description: "Delivery moves forward through review cycles, coaching, and practical milestones." },
	{ title: "Delivery & Growth", description: "Clients receive useful systems while team members build experience that actually counts." },
];

export const homeTestimonial = {
	quote:
		"Working with SaskTech Lab has been a game-changer for our business. Their team delivered a custom solution that perfectly addressed our needs, and we were impressed by their mentorship model that combines fresh perspectives with experienced oversight.",
	author: "Sarah Johnson",
	role: "CTO, AgriTech Solutions",
};

export const solutionPillars = [
	{
		title: "Software Engineering",
		description: "Our software engineering team develops custom applications tailored to your specific business requirements. From web and mobile applications to desktop software and system integrations, we deliver solutions that drive efficiency and innovation.",
		bullets: ["Web App Development — React, Angular, Vue.js, Node.js, Django, Laravel", "Desktop Software Development — C#, Java, Python, Electron, Qt", "Mobile App Development — React Native, Flutter, Swift, Kotlin", "LLM Integration — OpenAI, Hugging Face, custom LLMs for chatbots and workflows"],
	},
	{
		title: "Hardware Engineering",
		description: "From concept to prototype to production, we bring your hardware ideas to life with a focus on functionality, manufacturability, and reliability.",
		bullets: ["Industrial Design — SolidWorks, Fusion 360, AutoCAD", "Mechanical Design — simulation and optimization techniques", "IoT and Automation — Arduino, Raspberry Pi, RS232, PLC integration", "PCB Design — Altium Designer, KiCad, Eagle"],
	},
	{
		title: "IT Solutions",
		description: "Comprehensive IT services to support your technology infrastructure, from strategic planning to day-to-day operations and security.",
		bullets: ["IT Consulting — strategic planning, technology roadmaps, digital transformation", "IT Management — infrastructure support, monitoring, proactive maintenance", "Security — audits, vulnerability scanning, best practices", "Cloud Solutions — migration, scalability, modern cloud implementation"],
	},
];

export const industries = [
	{ name: "Agriculture", description: "Monitoring, analytics, and automation systems for farming and agri-tech operations." },
	{ name: "Manufacturing", description: "Digital tools and process systems that improve visibility and production efficiency." },
	{ name: "Healthcare", description: "Secure interfaces and analytics platforms that help teams work with sensitive operational data." },
	{ name: "Energy", description: "Monitoring systems and data flows for renewable energy and distributed assets." },
	{ name: "Public Sector", description: "Internal systems and service delivery tools for organizations that need clarity and reliability." },
	{ name: "Small Business", description: "Practical engineering support for teams that need modern systems without building a large internal department." },
];

export const caseStudies = [
	{
		title: "Smart Agriculture Monitoring System",
		sector: "Hardware & IT",
		category: "Hardware",
		summary:
			"An IoT-based solution for real-time crop monitoring and automated irrigation support across field conditions, using custom sensor nodes powered by solar energy.",
		results: [
			"30% reduction in water usage through optimized irrigation",
			"Early detection of potential crop issues",
			"Comprehensive data collection for long-term agricultural planning",
			"User-friendly mobile interface for remote monitoring",
			"Scalable architecture for broader deployment",
		],
		stack: ["Arduino", "ESP32", "LoRaWAN", "MQTT", "Node.js", "React Native", "AWS IoT"],
	},
	{
		title: "Manufacturing Process Optimization",
		sector: "Software",
		category: "Software",
		summary:
			"A custom software platform that improved production reporting and surfaced plant-floor performance more clearly, resulting in a 35% increase in production efficiency.",
		results: ["35% efficiency improvement in production", "Reduced manual reporting overhead", "Better real-time decision visibility"],
		stack: ["Web App", "Reporting", "Database", "Dashboard"],
	},
	{
		title: "Healthcare Data Analytics Platform",
		sector: "Software & Data",
		category: "Software",
		summary:
			"A secure platform for healthcare providers to analyze patient outcomes, improving data accessibility while maintaining strict privacy and compliance requirements.",
		results: ["Improved patient outcome visibility", "Streamlined data workflows for clinical teams", "Compliance-ready reporting"],
		stack: ["React", "Python", "PostgreSQL", "Secure APIs", "Data Visualization"],
	},
	{
		title: "Renewable Energy Monitoring System",
		sector: "Hardware & Software",
		category: "Hardware",
		summary:
			"A hardware and software stack for solar farm performance tracking, remote maintenance visibility, and centralized monitoring across distributed assets.",
		results: ["Faster anomaly detection", "Remote asset visibility across the farm", "Better maintenance planning"],
		stack: ["Sensors", "Telemetry", "Monitoring", "Cloud"],
	},
	{
		title: "Enterprise Cloud Migration",
		sector: "IT Solutions",
		category: "IT Solutions",
		summary:
			"Full cloud migration for an enterprise client, moving legacy on-premise infrastructure to a modern, scalable cloud environment with minimal downtime.",
		results: ["Reduced infrastructure costs", "Improved system uptime and scalability", "Modernized deployment pipeline"],
		stack: ["AWS", "Azure", "Terraform", "CI/CD", "Linux"],
	},
	{
		title: "Community Resource Mapping App",
		sector: "Software (Volunteer)",
		category: "Volunteer",
		summary:
			"A volunteer project to help a non-profit organization map and share community resources across Regina, improving access to support services for residents.",
		results: ["Interactive resource map for community members", "Searchable directory of local services", "Mobile-friendly and accessible design"],
		stack: ["React", "Google Maps API", "Node.js", "MongoDB"],
	},
];

export const leadership = [
	{
		name: "Maruf Ahmad",
		role: "Operation Lead",
		degree: "MASc in Electronic Systems Engineering, University of Regina",
		bio: "Maruf leads our operations and hardware engineering initiatives, bringing extensive experience in electronic systems design and project management.",
		linkedin: "https://www.linkedin.com/in/marufahmad2005/",
		photo: "/faces/maruf-ahmad.jpg",
	},
/* 	{
		name: "Mahibul Hasan",
		role: "DevOps Lead",
		degree: "MASc in Computer Science & Engineering, University of Regina",
		bio: "Mahibul oversees our DevOps practices and cloud infrastructure, ensuring seamless deployment and operation of our software solutions.",
		linkedin: "https://www.linkedin.com/in/mahibulhasan/",
		photo: "/faces/mahibul-hasan.jpg",
	}, */
	{
		name: "Sajid Khan Shaon",
		role: "Software Development Lead",
		degree: "MASc in Software Systems Engineering, University of Regina",
		bio: "Sajid leads our software development initiatives, specializing in web applications, mobile development, and AI integration.",
		linkedin: "https://www.linkedin.com/in/md-sajid-khan-7a193654/",
		photo: "/faces/sajid-khan-shaon.jpg",
	},
	{
		name: "Saima Zarka",
		role: "Graphic Designer | Digital Marketer | IT Consultant",
		degree: "BSc & MSc in IT, Jahangirnagar University, Bangladesh",
		bio: "Saima blends creativity with technical expertise, offering graphic design, digital marketing, and IT solutions to help brands grow and stand out online.",
		linkedin: "https://www.linkedin.com/in/saima-zarka/",
		photo: "/faces/saima-zarka.jpg",
	},
	{
		name: "Imran Bin Jafar",
		role: "Business Development & Partnerships Lead",
		degree: "MEng, University of Regina",
		bio: "Imran leads our business development and strategic partnerships, bringing extensive experience in IT solutions and enterprise technology. He focuses on building client relationships, identifying business opportunities, and expanding our presence across Saskatchewan's industrial and technology sectors.",
		linkedin: "https://www.linkedin.com/in/imranbinjafar/",
		photo: "/faces/imran-bin-jafar.jpg",
	},
	{
		name: "Afrina Najib",
		role: "HR & Administrative Officer",
		degree: "Master's in Human Resource Management, University of Regina",
		bio: "Afrina brings a unique combination of technical and administrative expertise to support our team operations and talent development.",
		linkedin: "https://www.linkedin.com/in/afrina-najib-13987915a/",
		photo: "/faces/afrina-najib.jpg",
	},
/* 	{
		name: "Nasik Sami Khan",
		role: "AI Engineer",
		degree: "MSc in Computer Science, University of Regina",
		bio: "Nasik specializes in AI/ML, Generative AI, and software engineering, with expertise in building scalable AI-powered solutions. He brings experience in LLM, NLP, cloud technologies, and enterprise-grade AI applications to drive innovation across our projects.",
		linkedin: "https://www.linkedin.com/in/nasiksami/",
		photo: "/faces/nasik-sami-khan.jpg",
	}, */
	{
		name: "S M Rizwan Islam Rhythm",
		role: "Front-End Developer | Business Analyst",
		degree: "MASc in Software Systems Engineering, University of Regina",
		bio: "Rizwan blends front-end skills with business insight to build intuitive and goal-driven user interfaces.",
		linkedin: "https://www.linkedin.com/in/s-m-rizwan-islam/",
		photo: "/faces/s-m-rizwan-islam-rhythm.jpg",
	},
	{
		name: "Sami Alkassab",
		role: "Embedded Systems Engineer",
		degree: "BSc in Electronic Systems Engineering, University of Regina",
		bio: "Sami specializes in embedded systems development, with a strong focus on hardware integration, firmware design, and real-time applications.",
		linkedin: "https://www.linkedin.com/in/sami-alkassab-1a7b1030a/",
		photo: "/faces/sami-alkassab.jpg",
	},
];

export const impactStats = [
	{ value: "25+", label: "Projects Completed" },
	{ value: "15+", label: "Graduates Supported" },
	{ value: "10+", label: "Industry Mentors" },
	{ value: "90%", label: "Client Satisfaction" },
];

export const aboutStory = [
	"SaskTech Lab was founded with a vision to address two critical challenges: providing high-quality engineering solutions to businesses while tackling the \"experience gap\" that many recent graduates face when entering the job market.",
	"Our founders, experienced engineers themselves, recognized that many talented graduates struggle to find meaningful employment due to lack of practical experience, while companies often hesitate to hire inexperienced talent despite their potential. This creates a paradoxical situation where graduates need experience to get hired, but need jobs to gain experience.",
	"SaskTech Lab was created as a solution to this problem. By establishing a company that combines commercial engineering services with a structured talent development program, we've created an environment where recent graduates can gain valuable industry experience while delivering real value to clients under the guidance of experienced professionals.",
	"Based in Regina, Saskatchewan, we've built a model that benefits all stakeholders: clients receive quality engineering solutions, graduates gain practical experience, and experienced professionals have the opportunity to share their knowledge while working on interesting projects.",
	"Today, SaskTech Lab continues to grow its impact, expanding both our service offerings and our talent development initiatives to create a sustainable pipeline of skilled engineers for the technology industry.",
];

export const aboutApproach = [
	{
		title: "Client Projects",
		description: "We take on commercial engineering projects across software, hardware, and IT domains, generating revenue while providing real-world experience for our team.",
	},
	{
		title: "Mentorship Structure",
		description: "Experienced industry professionals work with us as mentors and advisors, guiding junior team members and ensuring quality deliverables.",
	},
	{
		title: "Talent Development",
		description: "Recent graduates work on real projects under guidance, building practical skills and professional experience.",
	},
	{
		title: "Project Allocation",
		description: "We maintain a mix of commercial and volunteer projects, allocating them based on team skills and development needs.",
	},
];

export const modelSafeguards = [
	"Mentor-led planning and scoping",
	"Structured reviews during implementation",
	"Clear milestones and feedback loops",
	"Quality assurance before delivery",
	"Documentation and knowledge transfer",
];

export const deliverySteps = [
	{
		title: "Project Acquisition",
		description: "Commercial projects from businesses, volunteer initiatives with non-profits, research collaborations, and internal product development projects.",
	},
	{
		title: "Team Assembly",
		description: "Assessment of project requirements and complexity. Matching team member skills and development goals with appropriate mentor expertise.",
	},
	{
		title: "Mentored Execution",
		description: "Regular check-ins and reviews with mentors, structured knowledge transfer, progressive responsibility as competence develops, and quality assurance through mentor oversight.",
	},
	{
		title: "Skill Development Tracking",
		description: "Individual development plans, regular skill assessments, documentation of project contributions and achievements, and portfolio development for career advancement.",
	},
	{
		title: "Career Progression",
		description: "Internal advancement opportunities, support for those seeking positions elsewhere, ongoing connection through alumni network, and potential transition to mentor roles.",
	},
];

export const careerBenefits = [
	{
		title: "Real-World Projects",
		description: "Work on actual client projects that solve real business problems, not simulations or training exercises.",
	},
	{
		title: "Expert Mentorship",
		description: "Learn directly from experienced professionals who provide guidance, feedback, and industry insights.",
	},
	{
		title: "Diverse Experience",
		description: "Gain exposure to different industries, technologies, and project types to build a well-rounded skill set.",
	},
	{
		title: "Career Growth",
		description: "Build a portfolio of real projects and develop the skills needed to advance your career.",
	},
];

export const careerProgram = [
	{
		stage: "Junior Associate",
		duration: "0–12 months",
		points: [
			"Building technical fundamentals",
			"Learning industry best practices",
			"Developing professional communication",
			"Contributing to team projects",
		],
	},
	{
		stage: "Associate",
		duration: "1–2 years",
		points: [
			"Leading specific project components",
			"Developing specialized skills",
			"Contributing to project planning",
			"Mentoring newer team members",
		],
	},
	{
		stage: "Senior Associate",
		duration: "2+ years",
		points: [
			"Leading project teams",
			"Client communication and management",
			"Technical decision-making",
			"Mentoring junior team members",
		],
	},
	{
		stage: "Career Advancement",
		duration: "Ongoing",
		points: [
			"Leadership roles within SaskTech Lab",
			"Transition to mentor/advisor roles",
			"Advanced positions at other companies",
			"Ongoing connection through our alumni network",
		],
	},
];

export const openRoles = [
	{
		title: "Junior Software Developer",
		location: "Regina, SK (Hybrid)",
		type: "Full-time",
		points: [
			"Recent graduate in Computer Science or Software Engineering",
			"Basic knowledge of HTML, CSS, JavaScript",
			"Familiarity with Python, Java, or C#",
			"Eagerness to learn and grow",
			"Good communication and teamwork",
		],
	},
	{
		title: "Junior Hardware Engineer",
		location: "Regina, SK (On-site)",
		type: "Full-time",
		points: [
			"Recent graduate in Electrical Engineering or related field",
			"Knowledge of circuits and components",
			"PCB design familiarity",
			"IoT and embedded systems interest",
			"Hands-on problem solving",
		],
	},
	{
		title: "IT Support Volunteer",
		location: "Regina, SK (Hybrid)",
		type: "Part-time (5–10 hrs/week)",
		points: [
			"Basic computer/networking knowledge",
			"Interest in IT support",
			"3-month availability",
			"Good communication",
			"Commitment to service",
		],
	},
	{
		title: "Software Development Mentor",
		location: "Remote (Occasional)",
		type: "Part-time (5–10 hrs/week)",
		points: [
			"5+ years software development experience",
			"Modern web tech stack familiarity",
			"Experience mentoring or leading teams",
			"Strong technical and teaching skills",
			"Passion for helping others grow",
		],
	},
];

export const contactFaq = [
	{
		question: "How does your project process work?",
		answer: "Our process begins with an initial consultation to understand your needs. We then assemble a team of junior talent and experienced mentors matched to your project requirements. After developing a detailed proposal and timeline, we execute the project with regular check-ins and updates. Our mentorship model ensures quality while developing our team members' skills.",
	},
	{
		question: "What types of projects do you take on?",
		answer: "We work on a wide range of projects across software development, hardware engineering, and IT solutions. This includes web and mobile applications, IT systems, electronic prototypes, cloud migrations, and more.",
	},
	{
		question: "How do you ensure quality with junior team members?",
		answer: "Quality is ensured through our mentorship model, where experienced professionals guide and review the work of junior team members. We implement thorough quality assurance processes, regular code reviews, and testing protocols.",
	},
	{
		question: "Can I hire your graduates for my company?",
		answer: "Yes! We're open to our team members transitioning to opportunities with client companies after they've developed their skills with us. We're happy to discuss potential talent pipeline arrangements with companies looking to hire skilled engineers.",
	},
];
